import React, { useState } from 'react';
import {
  HeadphonesIcon, Search, Filter, Plus, MessageSquare,
  Clock, CheckCircle, AlertTriangle, User, Building2,
  ChevronRight, Paperclip, Send, MoreVertical
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const tickets = [
  {
    id: 'TKT-2847',
    subject: 'Ask Niko response accuracy issue',
    facility: 'Metro Health System',
    requester: 'Sarah Johnson',
    priority: 'high',
    status: 'open',
    category: 'Technical',
    created: '2 hours ago',
    lastUpdate: '30 min ago',
    messages: 3
  },
  {
    id: 'TKT-2846',
    subject: 'Need help configuring department permissions',
    facility: 'Community Hospital Network',
    requester: 'Michael Chen',
    priority: 'medium',
    status: 'in-progress',
    category: 'Configuration',
    created: '4 hours ago',
    lastUpdate: '1 hour ago',
    messages: 5
  },
  {
    id: 'TKT-2845',
    subject: 'Billing inquiry - Enterprise plan features',
    facility: 'Valley Health Partners',
    requester: 'Lisa Anderson',
    priority: 'low',
    status: 'open',
    category: 'Billing',
    created: '1 day ago',
    lastUpdate: '6 hours ago',
    messages: 2
  },
  {
    id: 'TKT-2844',
    subject: 'Request for additional training materials',
    facility: 'Sunrise Long-Term Care',
    requester: 'Robert Williams',
    priority: 'low',
    status: 'resolved',
    category: 'Training',
    created: '2 days ago',
    lastUpdate: '1 day ago',
    messages: 8
  },
  {
    id: 'TKT-2843',
    subject: 'Integration with existing EHR system',
    facility: 'Regional Medical Center',
    requester: 'Emily Davis',
    priority: 'high',
    status: 'in-progress',
    category: 'Integration',
    created: '3 days ago',
    lastUpdate: '2 hours ago',
    messages: 12
  },
];

const stats = [
  { label: 'Open Tickets', value: 12, color: 'yellow' },
  { label: 'In Progress', value: 8, color: 'blue' },
  { label: 'Resolved Today', value: 15, color: 'green' },
  { label: 'Avg. Resolution', value: '4.2h', color: 'teal' },
];

const SupportPage = () => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState('all');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Support Center</h1>
          <p className="text-gray-500 mt-1">Manage customer support tickets and inquiries</p>
        </div>
        <Button className="btn-primary gap-2">
          <Plus className="w-4 h-4" />
          Create Ticket
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input placeholder="Search tickets..." className="pl-10" />
        </div>
        <div className="flex gap-2">
          {['all', 'open', 'in-progress', 'resolved'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-[var(--niko-teal)] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="divide-y divide-gray-100">
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => setSelectedTicket(ticket)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-mono text-gray-400">{ticket.id}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      ticket.priority === 'high'
                        ? 'bg-red-100 text-red-700'
                        : ticket.priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {ticket.priority}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      ticket.status === 'open'
                        ? 'bg-blue-100 text-blue-700'
                        : ticket.status === 'in-progress'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {ticket.status}
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">{ticket.subject}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {ticket.facility}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {ticket.requester}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      {ticket.messages} messages
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Created {ticket.created}</p>
                  <p className="text-xs text-gray-500 mt-1">Updated {ticket.lastUpdate}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
